import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import useMonthOptions from '../hooks/useMonthOptions';

const ModalEditEnrollment = ({ open, onClose, enrollment, course, onSuccess, updateEnrollment }) => {
  const [form, setForm] = useState({
    startDate: '',
    paymentMethod: '',
    amount: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Hook per generare le opzioni dei mesi
  const monthOptions = useMonthOptions(course);

  useEffect(() => {
    if (enrollment) {
      setForm({
        startDate: enrollment.startDate ? dayjs(enrollment.startDate).format('YYYY-MM-DD') : '',
        paymentMethod: enrollment.paymentMethod || '',
        amount: enrollment.amount || ''
      });
    }
  }, [enrollment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        ...form,
        amount: Number(form.amount)
      };

      const result = await updateEnrollment(enrollment._id, updateData);
      
      if (result.success) {
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError(result.error || 'Errore durante la modifica dell\'iscrizione');
      }
    } catch (err) {
      setError('Errore durante la modifica dell\'iscrizione');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({
      startDate: '',
      paymentMethod: '',
      amount: ''
    });
    setError('');
    onClose();
  };

  if (!enrollment) return null;

  const user = enrollment.user || enrollment.userId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Modifica Iscrizione - {user?.name} {user?.surname}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Corso: <strong>{course?.name}</strong>
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Iscrizione a partire da</InputLabel>
                <Select
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                  label="Iscrizione a partire da"
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Metodo di pagamento</InputLabel>
                <Select
                  name="paymentMethod"
                  value={form.paymentMethod}
                  onChange={handleChange}
                  label="Metodo di pagamento"
                >
                  <MenuItem value="monthly">Mensile</MenuItem>
                  <MenuItem value="annual1">Annuale (1 pagamento)</MenuItem>
                  <MenuItem value="annual3">Annuale (3 rate)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Importo"
                name="amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Importo personalizzato in euro"
              />
            </Grid>

            {error && (
              <Grid size={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !form.startDate || !form.paymentMethod || !form.amount}
        >
          {loading ? <CircularProgress size={24} /> : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditEnrollment;
